import { useState, useEffect } from 'react';
import { X } from 'lucide-react';

interface AddIgnModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (game: string, ignName: string, ignId: string) => void;
    /** When set, the game dropdown is locked to this value (used from invite accept flow) */
    lockedGame?: string;
    /** Existing IGN games the user already has — to filter dropdown options */
    existingGames?: string[];
}

const ALL_GAMES = ['PUBG', 'Free Fire', 'Wild Rift', 'MLBB'];

export default function AddIgnModal({ isOpen, onClose, onSave, lockedGame, existingGames = [] }: AddIgnModalProps) {
    const [game, setGame] = useState(lockedGame || 'PUBG');
    const [ignName, setIgnName] = useState('');
    const [ignId, setIgnId] = useState('');

    // Reset form when modal opens
    useEffect(() => {
        if (isOpen) {
            setGame(lockedGame || 'PUBG');
            setIgnName('');
            setIgnId('');
        }
    }, [isOpen, lockedGame]);

    if (!isOpen) return null;

    const availableGames = lockedGame
        ? ALL_GAMES
        : ALL_GAMES.filter(g => !existingGames.includes(g));

    const handleSave = () => {
        if (!ignName.trim() || !ignId.trim()) return;
        onSave(game, ignName.trim(), ignId.trim());
    };

    return (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
            <div className="card glass-card animate-in fade-in" style={{ width: '100%', maxWidth: 400, position: 'relative' }}>
                <button style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }} onClick={onClose}><X size={20}/></button>
                <h3 style={{ marginBottom: 20, fontFamily: 'Outfit', fontWeight: 700 }}>
                    {lockedGame ? `Add ${lockedGame} IGN` : 'Add IGN'}
                </h3>
                {lockedGame && (
                    <p style={{ fontSize: '0.82rem', color: 'var(--warning)', marginBottom: 16, padding: '8px 12px', background: 'rgba(245,158,11,0.08)', borderRadius: 6, border: '1px solid rgba(245,158,11,0.2)' }}>
                        ⚠️ You need an in-game name for {lockedGame} to accept this squad invite.
                    </p>
                )}
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                        <label style={{ display: 'block', marginBottom: 6, fontSize: '0.85rem' }}>Select Game</label>
                        <select 
                            className="form-input" 
                            style={{ width: '100%', cursor: lockedGame ? 'not-allowed' : 'pointer', opacity: lockedGame ? 0.7 : 1 }} 
                            value={game} 
                            onChange={e => setGame(e.target.value)}
                            disabled={!!lockedGame}
                        >
                            {availableGames.map(g => (
                                <option key={g} value={g}>{g}</option>
                            ))}
                        </select>
                    </div>
                    
                    <div>
                        <label style={{ display: 'block', marginBottom: 6, fontSize: '0.85rem' }}>In-Game Name (IGN)</label>
                        <input type="text" className="form-input" placeholder="e.g. NinjaPro" value={ignName} onChange={e => setIgnName(e.target.value)} />
                    </div>

                    <div>
                        <label style={{ display: 'block', marginBottom: 6, fontSize: '0.85rem' }}>ID Number</label>
                        <input type="text" className="form-input" placeholder="e.g. 512345678" value={ignId} onChange={e => setIgnId(e.target.value)} />
                    </div>

                    <button 
                        className="btn btn-primary" 
                        style={{ marginTop: 10 }} 
                        onClick={handleSave}
                        disabled={!ignName.trim() || !ignId.trim()}
                    >
                        {lockedGame ? 'Save IGN & Accept Invite' : 'Add IGN'}
                    </button>
                </div>
            </div>
        </div>
    );
}
